import { useReducer } from "react";

import { CloseOutlined, SaveOutlined } from "@mui/icons-material";
import { Button, DialogActions, DialogContent, DialogTitle, FormControl, Grid, Stack } from "@mui/material";
import {
  isClearedFieldValue,
  isNumericOverride,
  numericValuesMatch,
  stressTestFieldDefaults,
} from "components/Core/overrideField";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import {
  ClearFieldAdornment,
  MASK_NUMERIC,
  StatefulControlledTextField,
} from "components/Core/StatefulControlledTextField";

export default function InsuranceStressTestParametersDialog({
  initialStressTestParameters,
  countyData,

  onChange,
  onClose,
}) {
  const [stressTestParameters, dispatchStressTestParameters] = useReducer(
    propUpdateReducer,
    initialStressTestParameters || countyData
  );
  const fieldDefaults = stressTestFieldDefaults(countyData);

  const applyField = (name, value, persist) => {
    dispatchStressTestParameters({
      type: "update",
      payload: { key: name, value },
    });
    if (!persist || numericValuesMatch(stressTestParameters?.[name], value)) return;
    onChange?.({ ...stressTestParameters, [name]: value });
  };

  const handleChangeStressTestParameters = (event) => {
    const name = event?.target?.name;
    const raw = event?.target?.value;
    const value = isClearedFieldValue(raw) && !isClearedFieldValue(fieldDefaults[name]) ? fieldDefaults[name] : raw;
    applyField(name, value, isClearedFieldValue(raw) && !isClearedFieldValue(value));
  };

  const handleClearField = (name) => {
    const value = fieldDefaults[name];
    if (isClearedFieldValue(value)) return;
    applyField(name, value, true);
  };

  const getClearAdornment = (name) => {
    if (!isNumericOverride(stressTestParameters?.[name], fieldDefaults[name])) return null;
    return <ClearFieldAdornment label={`reset ${name} to default`} onClear={() => handleClearField(name)} />;
  };

  const isValid = () => {
    return false;
  };

  const handleSaveClick = () => {
    onChange && onChange(stressTestParameters);
    onClose();
  };

  return (
    <>
      <DialogTitle>Stress Test Options</DialogTitle>

      <DialogContent>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12} md={6}>
            <fieldset>
              <legend>Farm Yields:</legend>
              <Grid container direction="column" spacing={2}>
                <Grid item xs={6}>
                  <FormControl>
                    <StatefulControlledTextField
                      label="Low Yield"
                      defaultValue={stressTestParameters?.yield_low ?? fieldDefaults.yield_low ?? 0}
                      restoreValue={fieldDefaults.yield_low}
                      onChange={handleChangeStressTestParameters}
                      name="yield_low"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      maskType={MASK_NUMERIC}
                      allowNull
                      endAdornment={getClearAdornment("yield_low")}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl>
                    <StatefulControlledTextField
                      label="High Yield"
                      defaultValue={
                        stressTestParameters?.yield_high ??
                        fieldDefaults.yield_high ??
                        (stressTestParameters?.yield_low ?? 0) * 1.5
                      }
                      restoreValue={fieldDefaults.yield_high}
                      onChange={handleChangeStressTestParameters}
                      name="yield_high"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      maskType={MASK_NUMERIC}
                      allowNull
                      endAdornment={getClearAdornment("yield_high")}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </fieldset>
          </Grid>
          <Grid item xs={12} md={6}>
            <fieldset>
              <legend>Price:</legend>
              <Grid container direction="column" spacing={2}>
                <Grid item xs={6}>
                  <FormControl>
                    <StatefulControlledTextField
                      label="Low Price"
                      defaultValue={stressTestParameters?.price_low ?? fieldDefaults.price_low ?? 0}
                      restoreValue={fieldDefaults.price_low}
                      onChange={handleChangeStressTestParameters}
                      name="price_low"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      maskType={MASK_NUMERIC}
                      allowNull
                      endAdornment={getClearAdornment("price_low")}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl>
                    <StatefulControlledTextField
                      label="High Price"
                      defaultValue={stressTestParameters?.price_high ?? fieldDefaults.price_high ?? 10}
                      restoreValue={fieldDefaults.price_high}
                      onChange={handleChangeStressTestParameters}
                      name="price_high"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      maskType={MASK_NUMERIC}
                      allowNull
                      endAdornment={getClearAdornment("price_high")}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </fieldset>
          </Grid>

          <Grid item xs={12} md={6}>
            <fieldset>
              <legend>County Yields:</legend>
              <Grid container direction="column" spacing={2}>
                <Grid item xs={6}>
                  <FormControl>
                    <StatefulControlledTextField
                      label="Low Yield"
                      defaultValue={stressTestParameters?.county_yield_low ?? fieldDefaults.county_yield_low ?? 0}
                      restoreValue={fieldDefaults.county_yield_low}
                      onChange={handleChangeStressTestParameters}
                      name="county_yield_low"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      maskType={MASK_NUMERIC}
                      allowNull
                      endAdornment={getClearAdornment("county_yield_low")}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl>
                    <StatefulControlledTextField
                      label="High Yield"
                      defaultValue={
                        stressTestParameters?.county_yield_high ??
                        fieldDefaults.county_yield_high ??
                        (stressTestParameters?.county_yield_low ?? 0) * 1.5
                      }
                      restoreValue={fieldDefaults.county_yield_high}
                      onChange={handleChangeStressTestParameters}
                      name="county_yield_high"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      maskType={MASK_NUMERIC}
                      allowNull
                      endAdornment={getClearAdornment("county_yield_high")}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </fieldset>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" color="secondary" autoFocus onClick={onClose}>
            <CloseOutlined sx={{ mr: 1 }} />
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveClick} disabled={!isValid}>
            <SaveOutlined sx={{ mr: 1 }} />
            Save
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
}
